<template>
  <client-only>
    <div class="w-fit max-w-full self-start min-w-0 rounded-full bg-black/85 inline-flex gap-2 items-center p-1 pr-3" v-if="currentProfileUser">
      <user-avatar :user="currentProfileUser" :size="size" class="flex-shrink-0 flex" />

      <span class="min-w-0 truncate text-white hover:underline cursor-pointer" :class="textSize" @click="goToUserProfile">{{ currentProfileUser.name }}</span>

      <u-tooltip v-if="status === 'none'" class="shrink-0" :text="`Add Connection`">
      <span  class="border-l border-white/20 flex items-center px-1.5 flex-shrink-0 self-stretch -mr-1.5 cursor-pointer" @click="addConnection">
        <u-icon :name="isProcessing ? COMMON_ICONS.loading : COMMON_ICONS.add" class="text-lg text-white" :class="isProcessing && 'animate-spin'" />
      </span>
      </u-tooltip>

      <u-dropdown :items="pendingDropdownItems" v-else-if="status === FRIEND_STATUS.sent || status === FRIEND_STATUS.pending" class="select-none shrink-0">
      <span class="border-l border-white/20 flex items-center px-1.5 flex-shrink-0 self-stretch -mr-1.5 cursor-pointer">
        <u-icon :name="isProcessing ? COMMON_ICONS.loading : 'i-ri-arrow-down-s-fill'" class="text-lg text-ice cursor-pointer" :class="isProcessing && 'animate-spin'" />
      </span>
        <template #pending>
          <div>
            {{currentProfileUser.name}} would like to connect.
            <div class="flex gap-2 mt-2">
              <u-button size="sm" block class="flex-1" icon="i-ri-check-line" @click="acceptPending">Accept</u-button>
              <u-button size="sm" block variant="outline" class="flex-1" :icon="COMMON_ICONS.close" @click="ignorePending">Ignore</u-button>
            </div>
          </div>
        </template>
      </u-dropdown>

      <user-settings-dropdown :user="currentProfileUser" v-else-if="status === FRIEND_STATUS.completed" :user-store="usersStore">
      <span class="border-l border-white/20 flex items-center px-1.5 flex-shrink-0 self-stretch -mr-1.5 cursor-pointer">
        <u-icon name="i-ri-arrow-down-s-fill" class="text-lg text-white cursor-pointer" />
      </span>
      </user-settings-dropdown>
    </div>
  </client-only>
</template>

<script setup>
  const props = defineProps({
    user: {
      type: Object,
      required: true,
    },
    size: {
      type: String,
      default: 'xs',
    }
  });

  const sessionStore = useSessionStore();
  const {isAuthenticated} = storeToRefs(sessionStore);
  const usersStore = makeUsersStore('owner-badge')();
  const {currentProfileUser} = storeToRefs(usersStore);

  const {refresh, pending, error} = await useAsyncData(
    `user-${props.user.id}`,
    () => usersStore.getUserProfile({userIdOrSlug: props.user.id})
  );

  const textSize = computed(() => {
    switch (props.size) {
      case '2xs':
        return 'text-xs';
      case 'xs':
        return 'text-sm';
      case 'sm':
        return 'text-sm';
      default:
        return 'text-base';
    }
  });


  const status = computed(() => {
    return FRIEND_STATUS[currentProfileUser?.value?.friendship?.status] || 'none';
  });

  const pendingDropdownItems = computed(() => {
    if (status.value === FRIEND_STATUS.sent) {
      return [[
        {
          label: 'Cancel Sent Request',
          icon: COMMON_ICONS.close,
          click: () => cancelSentRequest()
        }
      ]];
    } else if (status.value === FRIEND_STATUS.pending) {
      return [[
        {
          click: () => {},
          slot: 'pending',
          class: 'bg-white/80 text-charcoal cursor-default text-left border border-charcoal-300/50 shadow-sm'
        }
      ]];
    }
  });

  const isProcessing = ref(false);

  async function addConnection() {
    try {
      if (isProcessing.value) return;

      if (!isAuthenticated.value) {
        useCommonModals().openSignupFromShareModal({
          referrer: currentProfileUser.value,
          referralSource: 'connection-add',
          sendConnectionRequest: true
        });
      } else {
        isProcessing.value = true;
        await usersStore.addConnection({user: currentProfileUser.value});
        useSuccessToast().add({
          description: 'Connection request sent.'
        });
      }

    } catch (e) {
      useErrorToast().add();
    } finally {
      isProcessing.value = false;
    }
  }

  async function cancelSentRequest() {
    try {
      if (isProcessing.value) return;
      isProcessing.value = true;
      await usersStore.cancelSentRequest({user: currentProfileUser.value});
      useSuccessToast().add({
        description: 'Connection request cancelled.'
      });
    } catch (e) {
      useErrorToast().add();
    } finally {
      isProcessing.value = false;
    }
  }

  async function acceptPending() {
    try {
      if (isProcessing.value) return;
      isProcessing.value = true;
      await usersStore.acceptPendingRequest({user: currentProfileUser.value});
      useSuccessToast().add({
        description: 'Connection request accepted.'
      });
    } catch (e) {
      useErrorToast().add();
    } finally {
      isProcessing.value = true;
    }
  }

  async function ignorePending() {
    try {
      if (isProcessing.value) return;
      isProcessing.value = true;
      usersStore.ignorePendingRequest({user: currentProfileUser.value});
    } catch (e) {
      console.log(e)
      useErrorToast().add();
    } finally {
      isProcessing.value = false;
    }
  }

  function goToUserProfile() {
    navigateTo(useBuildRoute().toUserProfile({user: props.user}));
  }
</script>
